import { createContext, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, MessagesSquare, MessageCircleReply, Building2, Layers, QrCode, Users, Settings, Clock, FileBarChart2, Megaphone, CreditCard, Receipt, LifeBuoy, Sparkles } from "lucide-react";
import DashboardShell from "./DashboardShell";
import NotificationBell from "./NotificationBell";
import { useApi } from "../../lib/useApi";
import { useAuth } from "../../context/AuthContext";
import { COMPANY_KEY } from "../../lib/api";
import { Loader, ErrorState } from "../common/States";
import { ROLE } from "../../lib/labels";

const CompanyContext = createContext(null);
export const useCompany = () => useContext(CompanyContext);

const NAV = [
  { to: "/app", label: "Tableau de bord", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/app/feedbacks", label: "Feedbacks", icon: MessagesSquare, id: "feedbacks", perm: "feedback:read" },
  { to: "/app/agences", label: "Agences", icon: Building2, id: "agencies", perm: "structure:read" },
  { to: "/app/services", label: "Services", icon: Layers, id: "services", perm: "structure:read" },
  { to: "/app/qr-codes", label: "QR codes", icon: QrCode, id: "qrcodes", perm: "qr:read" },
  { to: "/app/conversations", label: "Conversations", icon: MessageCircleReply, id: "conversations", perm: "feedback:read" },
  { to: "/app/rapports", label: "Rapports", icon: FileBarChart2, id: "reports", perm: "reports:read" },
  { to: "/app/analyse-ia", label: "Analyse intelligente", icon: Sparkles, id: "insights", perm: "reports:read" },
  { to: "/app/equipe", label: "Collaborateurs", icon: Users, id: "team", perm: "team:read" },
  { to: "/app/communication", label: "Communications", icon: Megaphone, id: "communication", perm: "comms:read" },
  { to: "/app/abonnement", label: "Abonnement", icon: CreditCard, id: "billing", perm: "company:manage" },
  { to: "/app/factures", label: "Factures", icon: Receipt, id: "invoices", perm: "company:manage" },
  { to: "/app/parametres", label: "Paramètres", icon: Settings, id: "settings" },
  { to: "/app/support", label: "Support", icon: LifeBuoy, id: "support" },
];

const CompanySwitcher = ({ current }) => {
  const { user } = useAuth();
  if (!user?.memberships || user.memberships.length < 2) {
    return <p className="truncate text-sm font-semibold text-white" data-testid="sidebar-company-name">{current.name}</p>;
  }
  return (
    <select data-testid="company-switcher" value={current.id}
      className="w-full rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-sm text-white"
      onChange={(e) => { localStorage.setItem(COMPANY_KEY, e.target.value); window.location.assign("/app"); }}>
      {user.memberships.map((m) => <option key={m.company_id} value={m.company_id} className="text-slate-900">{m.company_name}</option>)}
    </select>
  );
};

const BANNERS = {
  draft: "Votre espace est en brouillon : confirmez l'email professionnel puis soumettez votre demande depuis Paramètres.",
  pending: "Votre entreprise est en attente de validation par l'équipe VOXA AFRIKA. Les dépôts publics seront ouverts après validation.",
  suspended: "Votre espace est suspendu pour facture impayée. Il sera réactivé automatiquement dès confirmation de votre paiement.",
};

const PendingBanner = ({ status }) => {
  if (!BANNERS[status]) return null;
  return (
    <div data-testid="company-pending-banner" className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2.5 text-sm text-amber-900">
      <Clock className="h-4 w-4 shrink-0" /> {BANNERS[status]}
    </div>
  );
};

export default function CompanyLayout() {
  const { data, loading, error, reload } = useApi("/company/context");
  const location = useLocation();
  if (loading) return <Loader />;
  if (error) return <div className="mx-auto max-w-lg p-10"><ErrorState message={error} onRetry={reload} /></div>;
  const can = (perm) => data.permissions.includes(perm);
  const billingLock = data.company.status === "suspended";
  if (billingLock && location.pathname !== "/app/abonnement") return <Navigate to="/app/abonnement" replace />;
  const nav = billingLock ? NAV.filter((n) => n.id === "billing" && can(n.perm)) : NAV.filter((n) => !n.perm || can(n.perm));
  return (
    <CompanyContext.Provider value={{ ...data, can, reload }}>
      <DashboardShell variant="company" nav={nav}
        subtitle={<><CompanySwitcher current={data.company} /><p className="mt-1 text-xs text-slate-400" data-testid="sidebar-role">{ROLE[data.role]}</p></>}
        banner={<PendingBanner status={data.company.status} />}
        headerSlot={<NotificationBell />} />
    </CompanyContext.Provider>
  );
}
